import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Subscribe to real-time updates for a user's profile.
 * Returns an unsubscribe function that should be called on cleanup.
 *
 * @param {string} uid - Firebase user ID
 * @param {Function} onChange - Callback receiving profile data (or null if not exists)
 * @returns {Function} Unsubscribe function
 *
 * @example
 * useEffect(() => {
 *   const unsub = subscribeToProfile(user.uid, setProfile);
 *   return () => unsub();
 * }, [user.uid]);
 */
export const subscribeToProfile = (uid, onChange) => {
  if (!uid) {
    return () => {};
  }
  const ref = doc(db, 'users', uid);
  return onSnapshot(ref, (snapshot) => {
    onChange(snapshot.exists() ? snapshot.data() : null);
  });
};

/**
 * Save or update user profile data in Firestore.
 * Uses merge to preserve existing fields not included in data.
 *
 * @param {string} uid - Firebase user ID
 * @param {Object} data - Profile data to save
 * @param {string} [data.name] - User's full name
 * @param {string} [data.phone] - User's phone number
 * @param {Array} [data.addresses] - Array of address objects
 * @returns {Promise<void>}
 *
 * @example
 * await saveProfile(userId, {
 *   name: 'John Doe',
 *   phone: '+919876543210',
 *   addresses: [{ label: 'Home', line1: '123 Main St', ... }]
 * });
 */
export const saveProfile = async (uid, data) => {
  const ref = doc(db, 'users', uid);
  await setDoc(
    ref,
    {
      ...data,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
};
